import type {
  DeviceRequest,
  DeviceResponse,
  SelectOption,
} from 'src/api/ProactApiClient';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export const DeviceDocumentTitle = 'Devices';

const DeviceNameFieldsMeta: IFormFieldV2[] = [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'serialNumber',
    label: 'Serial Number',
    type: 'text',
    required: false,
    validation: {maxLength: 255},
    size: 'md',
  },
];

const DeviceDetailFieldsMeta: IFormFieldV2[] = [
  {
    label: 'Location',
    name: 'heading1',
    type: 'heading',
  },
  {
    name: 'building',
    label: 'Building',
    type: 'text',
    required: false,
    validation: {maxLength: 100},
    size: 'sm',
  },
  {
    name: 'floor',
    label: 'Floor',
    type: 'text',
    required: false,
    validation: {maxLength: 100},
    size: 'sm',
  },
  {
    name: 'room',
    label: 'Room',
    type: 'text',
    required: false,
    validation: {maxLength: 100},
    size: 'sm',
  },
  {
    label: 'Equipment Details',
    name: 'heading2',
    type: 'heading',
  },
  {
    name: 'manufacturer',
    label: 'Manufacturer',
    type: 'text',
    required: false,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'model',
    label: 'Model',
    type: 'text',
    required: false,
    validation: {maxLength: 255},
    size: 'md',
  },
  {
    name: 'installDate',
    label: 'Install Date',
    type: 'date',
    required: false,
    size: 'sm',
  },
];

export const DeviceFormMeta = (
  sites: SelectOption[],
  equipmentTypes: SelectOption[]
): IFormFieldV2[] => [
  ...DeviceNameFieldsMeta,
  {
    name: 'siteId',
    label: 'Site',
    type: 'select',
    options: sites as ISelectOption[],
    required: true,
  },
  {
    name: 'equipmentTypeId',
    label: 'Equipment Type',
    type: 'select',
    options: equipmentTypes as ISelectOption[],
    required: true,
    description: 'Searchable — importing devices auto-creates new types.',
  },
  ...DeviceDetailFieldsMeta,
];

export const DeviceViewGeneralMeta: IFormFieldV2[] = [
  ...DeviceNameFieldsMeta,
  ...DeviceDetailFieldsMeta,
];

// The API sends explicit `null` where the generated TS types say `undefined`.
export const MapDeviceResponseToDeviceRequest = (
  data: DeviceResponse
): DeviceRequest => {
  return {
    id: data.id,
    // The 'select' field type stores the chosen option id as a string.
    siteId: Number(data.siteId),
    equipmentTypeId: Number(data.equipmentTypeId),
    name: data.name,
    serialNumber: data.serialNumber ?? undefined,
    building: data.building ?? undefined,
    floor: data.floor ?? undefined,
    room: data.room ?? undefined,
    manufacturer: data.manufacturer ?? undefined,
    model: data.model ?? undefined,
    installDate: data.installDate ?? undefined,
  };
};
